"use client";

import { c as _c } from "react/compiler-runtime";
import { deepEqual as isEqualFunc } from "fast-equals";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { PlotSidesForm } from "../plot-dimensions";
import { Input } from "../rhf/input";
import { ButtonActions } from "./action-buttons";
import { useFormContext } from "./context/FormProvider";
const flooringOptions = [{
  value: "marble",
  label: "Marble"
}, {
  value: "tiles",
  label: "Tiles"
}, {
  value: "wood",
  label: "Wood"
}, {
  value: "granite",
  label: "Granite"
}];
const furnishingOptions = [{
  value: "bareshell",
  label: "Bareshell"
}, {
  value: "partially-furnished",
  label: "Partially Furnished"
}, {
  value: "fully-furnished",
  label: "Fully Furnished"
}];
const yesNoOptions = [{
  value: "yes",
  label: "Yes"
}, {
  value: "no",
  label: "No"
}];
type AdditionalDetailsFormData = {
  totalFloors?: number;
  totalBasements?: number;
  registered?: string;
  isRectangular?: string;
  isCornerPlot?: string;
  notes?: string[];
  flooring?: string;
  plotSides?: {
    plotSides: number;
    details: Array<{
      dimension: string | number;
      isSideRoadFacing: "yes" | "no" | "";
      roadWidth?: string | number;
      facing?: string;
    }>;
  };
  furnishing?: string;
};
interface StepAdditionalProps {
  onNext: (data: AdditionalDetailsFormData) => void;
  onBack: () => void;
  propertyType?: string;
  isLoading?: boolean;
}
export function StepAdditional(t0) {
  const $ = _c(79);
  const {
    onNext,
    onBack,
    isLoading
  } = t0;
  const {
    state,
    updateStepData
  } = useFormContext();
  const contextProfile = state.profile as {
    propertyType?: {
      type?: string;
    };
  };
  const currentPropertyType = contextProfile.propertyType?.type || "";
  const contextAdditional = state.additional as Partial<AdditionalDetailsFormData>;
  let t1;
  if ($[0] !== contextAdditional) {
    t1 = {
      ...contextAdditional
    };
    $[0] = contextAdditional;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultValues = t1;
  let t2;
  if ($[2] !== defaultValues) {
    t2 = {
      defaultValues,
      mode: "onChange"
    };
    $[2] = defaultValues;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const form = useForm(t2);
  let control;
  let formState;
  let handleSubmit;
  let reset;
  let setValue;
  let useFormProps;
  let watch;
  if ($[4] !== form) {
    ({
      control,
      handleSubmit,
      setValue,
      watch,
      formState,
      reset,
      ...useFormProps
    } = form);
    $[4] = form;
    $[5] = control;
    $[6] = formState;
    $[7] = handleSubmit;
    $[8] = reset;
    $[9] = setValue;
    $[10] = useFormProps;
    $[11] = watch;
  } else {
    control = $[5];
    formState = $[6];
    handleSubmit = $[7];
    reset = $[8];
    setValue = $[9];
    useFormProps = $[10];
    watch = $[11];
  }
  let t3;
  if ($[12] !== control) {
    t3 = {
      control
    };
    $[12] = control;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const watchedValues = useWatch(t3) as AdditionalDetailsFormData;
  let t4;
  if ($[14] !== control) {
    t4 = {
      control,
      name: "isRectangular"
    };
    $[14] = control;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const isRectangular = useWatch(t4);
  let t5;
  if ($[16] !== watchedValues) {
    t5 = watchedValues || {};
    $[16] = watchedValues;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const memoizedWatchedValues = t5;
  let t6;
  let t7;
  if ($[18] !== isRectangular || $[19] !== setValue) {
    t6 = () => {
      if (isRectangular === "yes") {
        setValue("plotSides.plotSides", 4, {
          shouldDirty: true
        });
      }
    };
    t7 = [isRectangular, setValue];
    $[18] = isRectangular;
    $[19] = setValue;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  useEffect(t6, t7);
  const prevWatched = useRef(memoizedWatchedValues);
  const updateTimeoutRef = useRef(null);
  let t8;
  let t9;
  if ($[22] !== memoizedWatchedValues || $[23] !== updateStepData) {
    t8 = () => {
      if (!isEqualFunc(prevWatched.current, memoizedWatchedValues)) {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        updateTimeoutRef.current = setTimeout(() => {
          updateStepData("additional", memoizedWatchedValues);
          prevWatched.current = memoizedWatchedValues;
        }, 300);
      }
      return () => {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
      };
    };
    t9 = [memoizedWatchedValues, updateStepData];
    $[22] = memoizedWatchedValues;
    $[23] = updateStepData;
    $[24] = t8;
    $[25] = t9;
  } else {
    t8 = $[24];
    t9 = $[25];
  }
  useEffect(t8, t9);
  const isApartment = currentPropertyType === "apartment";
  const isHouse = currentPropertyType === "house";
  const isVilla = currentPropertyType === "villa";
  const isPlot = currentPropertyType === "plot";
  let t10;
  if ($[26] !== onNext) {
    t10 = data => {
      onNext(data);
    };
    $[26] = onNext;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const onSubmit = t10;
  let t11;
  if ($[28] !== control || $[29] !== formState || $[30] !== handleSubmit || $[31] !== reset || $[32] !== setValue || $[33] !== useFormProps || $[34] !== watch) {
    t11 = {
      control,
      handleSubmit,
      setValue,
      watch,
      formState,
      reset,
      ...useFormProps
    };
    $[28] = control;
    $[29] = formState;
    $[30] = handleSubmit;
    $[31] = reset;
    $[32] = setValue;
    $[33] = useFormProps;
    $[34] = watch;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== handleSubmit || $[37] !== onSubmit) {
    t12 = handleSubmit(onSubmit);
    $[36] = handleSubmit;
    $[37] = onSubmit;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== control || $[40] !== isApartment || $[41] !== isHouse || $[42] !== isVilla) {
    t13 = (isApartment || isHouse || isVilla) && <div className="grid grid-cols-1 md:grid-cols-2 gap-2"><Input label="No Of Floors" name="totalFloors" type="number" control={control} /><Input label="No Of Basements" name="totalBasements" type="number" control={control} /></div>;
    $[39] = control;
    $[40] = isApartment;
    $[41] = isHouse;
    $[42] = isVilla;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== control) {
    t14 = <Input label="Registered" name="registered" type="radio" options={yesNoOptions} control={control} />;
    $[44] = control;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== control || $[47] !== isHouse || $[48] !== isPlot || $[49] !== isVilla) {
    t15 = (isPlot || isHouse || isVilla) && <><Input label="Plot is rectangular" name="isRectangular" type="radio" options={yesNoOptions} control={control} /><Input label="Is a corner Plot" name="isCornerPlot" type="radio" options={yesNoOptions} control={control} /></>;
    $[46] = control;
    $[47] = isHouse;
    $[48] = isPlot;
    $[49] = isVilla;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== t14 || $[52] !== t15) {
    t16 = <div className="grid grid-cols-1 md:grid-cols-3 gap-2">{t14}{t15}</div>;
    $[51] = t14;
    $[52] = t15;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== isHouse || $[55] !== isPlot || $[56] !== isVilla) {
    t17 = (isHouse || isVilla || isPlot) && <PlotSidesForm name="plotSides" />;
    $[54] = isHouse;
    $[55] = isPlot;
    $[56] = isVilla;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== control || $[59] !== isApartment || $[60] !== isHouse || $[61] !== isVilla) {
    t18 = (isApartment || isHouse || isVilla) && <><Input type="radio" label="Flooring" name="flooring" itemsClassName="grid grid-cols-2 md:grid-cols-4 gap-2" options={flooringOptions} control={control} /><Input type="radio" label="Furnishing" itemsClassName="grid grid-cols-1 md:grid-cols-3 gap-2" name="furnishing" options={furnishingOptions} control={control} /></>;
    $[58] = control;
    $[59] = isApartment;
    $[60] = isHouse;
    $[61] = isVilla;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== control) {
    t19 = <Input label="Notes" type="textarea" multiline="true" rows={2} name="notes" control={control} />;
    $[63] = control;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== isLoading || $[66] !== onBack) {
    t20 = <ButtonActions next={true} onBack={onBack} isLoading={isLoading} />;
    $[65] = isLoading;
    $[66] = onBack;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== t12 || $[69] !== t13 || $[70] !== t16 || $[71] !== t17 || $[72] !== t18 || $[73] !== t19 || $[74] !== t20) {
    t21 = <form onSubmit={t12} className="space-y-6 mx-auto p-2">{t13}{t16}{t17}{t18}{t19}{t20}</form>;
    $[68] = t12;
    $[69] = t13;
    $[70] = t16;
    $[71] = t17;
    $[72] = t18;
    $[73] = t19;
    $[74] = t20;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] !== t11 || $[77] !== t21) {
    t22 = <FormProvider {...t11}>{t21}</FormProvider>;
    $[76] = t11;
    $[77] = t21;
    $[78] = t22;
  } else {
    t22 = $[78];
  }
  return t22;
}